import type { Key } from "react-stately";

import { Outlet, useMatchRoute, useRouter } from "@tanstack/react-router";
import { ChevronLeftIcon } from "lucide-react";
import { useCallback } from "react";

import { Button } from "@/components/button";
import { Tab, TabList, TabPanel, Tabs } from "@/components/tabs";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useUser } from "@/hooks/useUser";

import type { BillingTabId } from "../config/billing-tab-routes";

import {
  BILLING_TAB_CONFIG,
  BILLING_TABS,
  billingTabLabel,
} from "../config/billing-tab-routes";
import { BillingBanners } from "./banners/BillingBanners";

interface BillingPageProps {
  readonly projectId: string;
}

export function BillingPage({ projectId }: BillingPageProps) {
  const { history, navigate } = useRouter();
  const matchRoute = useMatchRoute();
  const { data: userData } = useUser();

  const getCurrentTab = (): BillingTabId => {
    if (matchRoute({ to: "/billing/$projectId/overview" }))
      return BILLING_TABS.OVERVIEW;
    if (matchRoute({ to: "/billing/$projectId/invoices" }))
      return BILLING_TABS.INVOICES;
    if (matchRoute({ to: "/billing/$projectId/payment-methods" }))
      return BILLING_TABS.PAYMENT_METHODS;
    if (matchRoute({ to: "/billing/$projectId/history" }))
      return BILLING_TABS.HISTORY;

    return BILLING_TABS.OVERVIEW;
  };

  const selectedTab = getCurrentTab();
  const tabLabel = billingTabLabel(selectedTab) ?? "Overview";

  const project = userData?.projects.find((p) => p.id === Number(projectId));
  const accountName = project?.name ?? "";

  usePageTitle(`Billing - ${tabLabel}`);

  const handleTabChange = useCallback(
    (key: Key) => {
      navigate({
        params: { projectId },
        to: `/billing/$projectId/${key.toString()}`,
      });
    },
    [projectId, navigate],
  );

  return (
    <div className="relative bg-bg-tertiary sm:p-xl">
      <div className="mx-auto flex w-full flex-col">
        <div className="flex items-start gap-2xl sm:rounded-t-xl sm:border-b sm:border-b-border-secondary bg-bg-primary pt-xl pb-2xl px-3xl">
          <Button
            className="mt-xs shrink-0"
            color="gray"
            isIconOnly
            onPress={() => history.go(-1)}
            size="lg"
            variant="secondary"
          >
            <ChevronLeftIcon size={20} />
          </Button>
          <div className="flex flex-1 flex-wrap items-center justify-between gap-y-lg">
            <span className="text-2xl font-semibold text-text-primary">
              {accountName}
            </span>
            <Button size="lg">Change subscription</Button>
          </div>
        </div>

        <Tabs
          className="rounded-b-xl bg-bg-primary"
          onSelectionChange={handleTabChange}
          selectedKey={selectedTab}
        >
          <div className="overflow-x-auto scrollbar-none sm:overflow-x-visible px-3xl pt-2xl">
            <TabList
              aria-label="Billing Management"
              className="flex min-w-full flex-nowrap sm:flex-wrap"
            >
              {BILLING_TAB_CONFIG.map((tab) => (
                <Tab id={tab.id} key={tab.id}>
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </div>

          {BILLING_TAB_CONFIG.map((tab) => (
            <TabPanel className="flex-initial py-0" id={tab.id} key={tab.id}>
              {selectedTab === tab.id && (
                <>
                  {project && (
                    <BillingBanners project={project} tab={selectedTab} />
                  )}
                  <Outlet />
                </>
              )}
            </TabPanel>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
